import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { TabProps } from "../../../constants/Interface";
import {
  getEmailConfig,
  getCompany,
  updateCompany,
  createCompany,
  sendTestEmail,
} from "../../../apis/company";
import { useGlobalSnackBar } from "../../SnackbarContext";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const EmailTab: React.FC<TabProps> = ({ value }) => {
  const currentUser = useSelector((state: RootState) => state.adminAuth.admin);
  const { showSnackBar } = useGlobalSnackBar();

  const [emailConfig, setEmailConfig] = useState<any>(null);
  const [companyId, setCompanyId] = useState<string>("");
  const [apiKey, setApiKey] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [testTo, setTestTo] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAdmin = currentUser?.permission === "Administrator";

  const fetchConfig = async () => {
    try {
      const config = await getEmailConfig();
      setEmailConfig(config);
      setFromEmail(config.fromEmail || "");

      const companyData = await getCompany();
      if (companyData?.[0]?.ID) {
        setCompanyId(companyData[0].ID);
      }
    } catch (err) {
      console.error("Failed to fetch email config", err);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const payload: any = { from_email: fromEmail };
      if (apiKey) {
        payload.sendgrid_api_key = apiKey;
      }

      if (companyId) {
        await updateCompany({ ...payload, ID: companyId });
      } else {
        await createCompany(payload);
      }

      showSnackBar("Email settings saved.");
      setIsEditing(false);
      setApiKey("");
      await fetchConfig();
    } catch (err) {
      showSnackBar("Failed to save email settings.", "error");
    }
    setSaving(false);
  };

  const handleTest = async () => {
    if (!testTo) {
      showSnackBar("Enter a recipient email address.", "warning");
      return;
    }
    setTesting(true);
    try {
      const res = await sendTestEmail({ to: testTo });
      if (res.success) {
        showSnackBar("Test email sent successfully.");
      } else {
        showSnackBar(res.message || "Failed to send test email.", "error");
      }
    } catch (err: any) {
      showSnackBar("Failed to send test email.", "error");
    }
    setTesting(false);
  };

  if (!isAdmin) {
    return (
      <CustomTabPanel value={value} index={3}>
        <Alert severity="warning" sx={{ mt: 2 }}>
          Only administrators can access email configuration.
        </Alert>
      </CustomTabPanel>
    );
  }

  return (
    <CustomTabPanel value={value} index={3}>
      <Box sx={{ maxWidth: 600, mx: "auto" }}>
        <Typography variant="h5" align="center" mt={2} mb={3}>
          Email Configuration
        </Typography>

        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
          Changing the email API key will affect all outgoing emails (invitations,
          password resets, quote notifications). Ensure the new key is valid before saving.
        </Alert>

        {/* Current status */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Status
          </Typography>
          {emailConfig ? (
            <>
              <Typography variant="body2">
                API Key: {emailConfig.hasApiKey ? (
                  <span style={{ color: "#2e7d32", fontWeight: 600 }}>Configured</span>
                ) : (
                  <span style={{ color: "#d32f2f", fontWeight: 600 }}>Not configured</span>
                )}
                {emailConfig.hasApiKey && (
                  <span style={{ color: "#666", marginLeft: 8 }}>
                    ({emailConfig.maskedApiKey})
                  </span>
                )}
              </Typography>
              <Typography variant="body2">
                From Email: {emailConfig.fromEmail || <span style={{ color: "#d32f2f" }}>Not set</span>}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Source: {emailConfig.source === "database" ? "Portal settings" : "Server environment"}
              </Typography>
            </>
          ) : (
            <CircularProgress size={20} />
          )}
        </Box>

        {/* Edit form */}
        {!isEditing ? (
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Button variant="outlined" onClick={() => setIsEditing(true)}>
              Edit Email Settings
            </Button>
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Leave the API key blank to keep the current key unchanged.
            </Alert>

            <TextField
              label="Resend API Key"
              fullWidth
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxx"
              sx={{ mb: 2 }}
            />

            <TextField
              label="From Email Address"
              fullWidth
              type="email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="noreply@yourcompany.com"
              sx={{ mb: 3 }}
              helperText="Must be a verified domain/sender in your Resend account."
            />

            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsEditing(false);
                  setApiKey("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => setConfirmOpen(true)}
                disabled={saving || (!apiKey && !fromEmail)}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : "Save Email Settings"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Test email */}
        <Typography variant="h6" align="center" mt={4} mb={2}>
          Send Test Email
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            label="Recipient Email"
            fullWidth
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="test@example.com"
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleTest}
            disabled={testing || !emailConfig?.hasApiKey}
            sx={{ minWidth: 120, py: 1 }}
          >
            {testing ? <CircularProgress size={20} color="inherit" /> : "Send Test"}
          </Button>
        </Box>
        {!emailConfig?.hasApiKey && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
            Configure an API key before sending test emails.
          </Typography>
        )}
      </Box>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Email Settings Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Changing email settings will immediately affect all outgoing emails
            from this portal, including invitations, password resets, and quote
            notifications.
            {apiKey && (
              <><br /><br /><strong>You are updating the Resend API key.</strong> If the new key
              is invalid, no emails will be sent until it is corrected.</>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} color="warning" variant="contained">
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>
    </CustomTabPanel>
  );
};
